import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { stagePlayer } from '../audio/stagePlayer.js';

/**
 * VolumeControl — the round speaker button + its slider popover.
 *
 * ONE control, used everywhere audio can be heard: the desktop Activity TopBar
 * and the game-select screen (which is now scored, so it needs the same knob).
 * Per-user semantics: the slider drives stagePlayer's MASTER volume (narration
 * + ambient + stingers + UI blips, ramped live and persisted across mounts),
 * mute zeroes the same master gain. Each iframe is its own audio instance.
 *
 * Mute state lives here by default; pass `muted`/`onMuted` to hoist it when the
 * caller needs to render the icon elsewhere too (TopBar's collapsed button).
 */
export function VolumeControl({
  muted: mutedProp,
  onMuted,
  onOpenChange,
}: {
  muted?: boolean;
  onMuted?: (m: boolean) => void;
  /** Notified when the popover opens/closes (TopBar closes its roster on open). */
  onOpenChange?: (open: boolean) => void;
} = {}) {
  const [open, setOpen] = useState(false);
  const [mutedLocal, setMutedLocal] = useState(false);
  const muted = mutedProp ?? mutedLocal;

  function setMuted(m: boolean) {
    setMutedLocal(m);
    onMuted?.(m);
  }

  function toggleOpen() {
    setOpen((o) => {
      onOpenChange?.(!o);
      return !o;
    });
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        className="rm-hover"
        style={iconBtn}
        onClick={toggleOpen}
        aria-label="Volume"
        aria-expanded={open}
      >
        {muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
      </button>
      {open && <VolumePopover muted={muted} onMuted={setMuted} onClose={() => setOpen(false)} />}
    </div>
  );
}

function VolumePopover({
  muted,
  onMuted,
  onClose,
}: {
  muted: boolean;
  onMuted: (m: boolean) => void;
  onClose: () => void;
}) {
  // Initialize from the player so the slider survives popover close/reopen.
  const [volume, setVolume] = useState(() => stagePlayer.masterVolume);
  const ref = useRef<HTMLDivElement>(null);

  // Outside click / Escape close.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    // Defer so the opening click doesn't immediately close it.
    const t = setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  function applyVolume(v: number) {
    setVolume(v);
    // Master gain: ALL audio (narration + ambient + stingers) ramps live —
    // and the value persists in stagePlayer across popover unmounts.
    stagePlayer.setVolume(v);
  }
  function toggleMute() {
    const next = !muted;
    stagePlayer.setMuted(next);
    onMuted(next);
  }

  return (
    <div ref={ref} style={popoverPanel} role="group" aria-label="Volume">
      <button
        type="button"
        className="rm-hover"
        onClick={toggleMute}
        style={{ ...iconBtn, width: 34, height: 34 }}
        aria-label={muted ? 'Unmute' : 'Mute'}
      >
        {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
      </button>
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={volume}
        disabled={muted}
        onChange={(e) => applyVolume(Number(e.target.value))}
        aria-label="Volume"
        style={{ width: 130, accentColor: 'var(--gold-4, #c9923f)', opacity: muted ? 0.4 : 1 }}
      />
      <span
        style={{
          fontFamily: 'var(--font-ui)',
          fontSize: 11,
          color: 'var(--text-tertiary)',
          width: 26,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {Math.round(volume * 100)}
      </span>
    </div>
  );
}

// ─── Styles (shared with TopBar's sibling buttons — keep in step) ─────────────

export const volumeIconBtn: CSSProperties = {
  width: 40,
  height: 40,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  background: 'rgba(6,4,6,0.6)',
  border: '1px solid rgba(201,164,78,0.35)',
  color: 'var(--text-primary)',
  cursor: 'pointer',
  transition:
    'border-color var(--duration-fast, 160ms) var(--ease-standard), background var(--duration-fast, 160ms) var(--ease-standard), filter var(--duration-fast, 160ms) var(--ease-standard), transform var(--duration-fast, 160ms) var(--ease-standard)',
};

const iconBtn = volumeIconBtn;

const popoverPanel: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  right: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 14px',
  borderRadius: 'var(--radius-lg, 12px)',
  background: 'rgba(6,4,6,0.92)',
  border: '1px solid rgba(201,164,78,0.25)',
  boxShadow: '0 10px 34px rgba(0,0,0,0.6)',
  zIndex: 50,
};
